/**
 * Geometry utility functions for floor plan calculations
 */

/**
 * Project a 2D point onto a wall segment, clamped to the segment bounds.
 *
 * Canonical home for what used to be three byte-for-byte copies
 * (`projectPointOntoSegment` private in floorPlanFixtureMeasurements.js and
 * `projectPointOntoWall` exported from measurementUtils.js / measurementUtilsV2.js).
 *
 * `t` is the clamped parameter in [0, 1] along start→end; `distance` is the
 * perpendicular distance from `point` to the *clamped* projection (so a point
 * that projects past an endpoint reports the distance to that endpoint).
 *
 * @param {[number, number]} point - [x, z]
 * @param {{ start: [number, number], end: [number, number] }} wallSegment
 * @returns {{ point: [number, number], t: number, distance: number } | null}
 *          null when the segment has zero length.
 */
export const projectPointOntoSegment = (point, wallSegment) => {
  const [px, py] = point;
  const [sx, sy] = wallSegment.start;
  const [ex, ey] = wallSegment.end;

  const wallVec = [ex - sx, ey - sy];
  const wallLength = Math.sqrt(
    wallVec[0] * wallVec[0] + wallVec[1] * wallVec[1]
  );
  if (wallLength === 0) return null;

  const pointVec = [px - sx, py - sy];
  const dotProduct = pointVec[0] * wallVec[0] + pointVec[1] * wallVec[1];
  const t = dotProduct / (wallLength * wallLength);
  const clampedT = Math.max(0, Math.min(1, t));

  const projectedPoint = [
    sx + clampedT * wallVec[0],
    sy + clampedT * wallVec[1],
  ];

  return {
    point: projectedPoint,
    t: clampedT,
    distance: Math.sqrt(
      Math.pow(px - projectedPoint[0], 2) + Math.pow(py - projectedPoint[1], 2)
    ),
  };
};

/**
 * Applies 3D rotation to a direction vector and projects to 2D XZ plane
 * @param {Object} rotation - Rotation object with x, y, z in radians
 * @param {Array} initialDirection - Initial direction vector [x, y, z] (default: [1, 0, 0])
 * @returns {Array} Normalized 2D direction vector [x, z]
 */
export const apply3DRotationTo2D = (rotation, initialDirection = [1, 0, 0]) => {
  // Get rotation angles
  const rx = rotation?.x || 0;
  const ry = rotation?.y || 0;
  const rz = rotation?.z || 0;

  // Start with the provided initial direction
  let dirX = initialDirection[0];
  let dirY = initialDirection[1];
  let dirZ = initialDirection[2];

  // Apply X rotation (around X axis)
  const cosX = Math.cos(rx);
  const sinX = Math.sin(rx);
  let tempY = dirY * cosX - dirZ * sinX;
  let tempZ = dirY * sinX + dirZ * cosX;
  dirY = tempY;
  dirZ = tempZ;

  // Apply Y rotation (around Y axis)
  const cosY = Math.cos(ry);
  const sinY = Math.sin(ry);
  let tempX = dirX * cosY + dirZ * sinY;
  tempZ = -dirX * sinY + dirZ * cosY;
  dirX = tempX;
  dirZ = tempZ;

  // Apply Z rotation (around Z axis)
  const cosZ = Math.cos(rz);
  const sinZ = Math.sin(rz);
  tempX = dirX * cosZ - dirY * sinZ;
  tempY = dirX * sinZ + dirY * cosZ;
  dirX = tempX;
  dirY = tempY;

  // Project to XZ plane for floor plan view and normalize
  const dirLength = Math.sqrt(dirX * dirX + dirZ * dirZ);
  if (dirLength > 0) {
    return [dirX / dirLength, dirZ / dirLength];
  }
  return [1, 0]; // Default direction if something goes wrong
};

/**
 * Calculates mitered corner point for wall intersections
 * @param {Array} prevPoint - Previous point [x, y]
 * @param {Array} currentPoint - Current corner point [x, y]
 * @param {Array} nextPoint - Next point [x, y]
 * @param {number} thickness - Wall thickness
 * @returns {Array} Mitered corner point [x, y]
 */
export const calculateMiterPoint = (
  prevPoint,
  currentPoint,
  nextPoint,
  thickness
) => {
  // Calculate vectors for the two wall segments
  const vec1 = [currentPoint[0] - prevPoint[0], currentPoint[1] - prevPoint[1]];
  const vec2 = [nextPoint[0] - currentPoint[0], nextPoint[1] - currentPoint[1]];

  // Normalize the vectors
  const len1 = Math.sqrt(vec1[0] * vec1[0] + vec1[1] * vec1[1]);
  const len2 = Math.sqrt(vec2[0] * vec2[0] + vec2[1] * vec2[1]);

  if (len1 === 0 || len2 === 0) return currentPoint;

  const norm1 = [vec1[0] / len1, vec1[1] / len1];
  const norm2 = [vec2[0] / len2, vec2[1] / len2];

  // Calculate perpendicular vectors (outward normals)
  const perp1 = [-norm1[1], norm1[0]];
  const perp2 = [-norm2[1], norm2[0]];

  // Calculate the bisector direction (flipped to point outward)
  const bisector = [-(perp1[0] + perp2[0]) / 2, -(perp1[1] + perp2[1]) / 2];

  // Normalize the bisector
  const bisectorLen = Math.sqrt(
    bisector[0] * bisector[0] + bisector[1] * bisector[1]
  );
  if (bisectorLen === 0) return currentPoint;

  const normBisector = [bisector[0] / bisectorLen, bisector[1] / bisectorLen];

  // Calculate the angle between the walls
  const dotProduct = norm1[0] * norm2[0] + norm1[1] * norm2[1];
  const angle = Math.acos(Math.max(-1, Math.min(1, dotProduct)));

  // Calculate miter length
  const miterLength = thickness / Math.sin(angle / 2);

  // Limit miter length to prevent extremely long miters
  const maxMiterLength = thickness * 3;
  const finalMiterLength = Math.min(miterLength, maxMiterLength);

  return [
    currentPoint[0] + normBisector[0] * finalMiterLength,
    currentPoint[1] + normBisector[1] * finalMiterLength,
  ];
};

/**
 * Checks if a point is near a wall segment
 * @param {number} worldX - World X coordinate
 * @param {number} worldY - World Y coordinate (Z in 3D)
 * @param {Object} wall - Wall object with center, dimensions, rotation
 * @param {number} threshold - Distance threshold
 * @returns {boolean} True if point is near wall
 */
export const isPointNearWall = (worldX, worldY, wall, threshold = 0.2) => {
  if (
    !wall.center ||
    wall.center.x === undefined ||
    wall.center.z === undefined ||
    !wall.dimensions
  )
    return false;

  const halfLength = wall.dimensions.x / 2;

  // Get wall direction from rotation
  // Use consistent rotation method for all walls
  let normalizedDir = apply3DRotationTo2D(wall.rotation);

  // Calculate start and end points using center.x and center.z (2D projection)
  const startX = wall.center.x - normalizedDir[0] * halfLength;
  const startY = wall.center.z - normalizedDir[1] * halfLength;
  const endX = wall.center.x + normalizedDir[0] * halfLength;
  const endY = wall.center.z + normalizedDir[1] * halfLength;

  // Check distance from point to line segment
  const dx = endX - startX;
  const dy = endY - startY;
  const t = Math.max(
    0,
    Math.min(
      1,
      ((worldX - startX) * dx + (worldY - startY) * dy) / (dx * dx + dy * dy)
    )
  );
  const nearestX = startX + t * dx;
  const nearestY = startY + t * dy;
  const distance = Math.sqrt(
    Math.pow(worldX - nearestX, 2) + Math.pow(worldY - nearestY, 2)
  );

  return distance <= threshold;
};

/**
 * Checks if a point is near a window segment
 * @param {number} worldX - World X coordinate
 * @param {number} worldY - World Y coordinate (Z in 3D)
 * @param {Object} window - Window object with center, dimensions, rotation
 * @param {number} threshold - Distance threshold
 * @returns {boolean} True if point is near window
 */
export const isPointNearWindow = (worldX, worldY, window, threshold = 0.3) => {
  if (
    !window.center ||
    window.center.x === undefined ||
    window.center.z === undefined ||
    !window.dimensions
  )
    return false;

  const halfWidth = window.dimensions.x / 2;

  // Get window direction from rotation
  const normalizedDir = apply3DRotationTo2D(window.rotation);

  // Calculate window endpoints using center.x and center.z
  const startX = window.center.x - normalizedDir[0] * halfWidth;
  const startY = window.center.z - normalizedDir[1] * halfWidth;
  const endX = window.center.x + normalizedDir[0] * halfWidth;
  const endY = window.center.z + normalizedDir[1] * halfWidth;

  // Check distance from point to line segment
  const dx = endX - startX;
  const dy = endY - startY;
  const t = Math.max(
    0,
    Math.min(
      1,
      ((worldX - startX) * dx + (worldY - startY) * dy) / (dx * dx + dy * dy)
    )
  );
  const nearestX = startX + t * dx;
  const nearestY = startY + t * dy;
  const distance = Math.sqrt(
    Math.pow(worldX - nearestX, 2) + Math.pow(worldY - nearestY, 2)
  );

  return distance <= threshold;
};

/**
 * Checks if a point is near an opening
 * @param {number} worldX - World X coordinate
 * @param {number} worldY - World Y coordinate (Z in 3D)
 * @param {Object} opening - Opening object
 * @param {number} threshold - Distance threshold
 * @returns {boolean} True if point is near opening
 */
export const isPointNearOpening = (
  worldX,
  worldY,
  opening,
  threshold = 0.3
) => {
  // Openings use same logic as windows
  return isPointNearWindow(worldX, worldY, opening, threshold);
};

/**
 * Checks if a point is within an object's bounding box
 * @param {number} worldX - World X coordinate
 * @param {number} worldY - World Y coordinate (Z in 3D)
 * @param {Object} obj - Object with center and dimensions
 * @returns {boolean} True if point is inside object bounds
 */
export const isPointInObjectBounds = (worldX, worldY, obj) => {
  if (
    !obj.center ||
    obj.center.x === undefined ||
    obj.center.z === undefined ||
    !obj.dimensions
  )
    return false;

  // Enforce a minimum hit-test size so thin objects (mirrors, wall-lights)
  // are still clickable. 0.15m ≈ 6 inches minimum tap target per axis.
  const MIN_HIT_SIZE = 0.15;
  const width = Math.max(obj.dimensions.x, MIN_HIT_SIZE);
  const depth = Math.max(obj.dimensions.z, MIN_HIT_SIZE);

  // If the object has rotation, test in the object's local coordinate space
  if (obj.rotation) {
    const dir = apply3DRotationTo2D(obj.rotation);
    // dir = [cos, sin] of the object's local X axis in world space
    const cosA = dir[0];
    const sinA = dir[1];

    // Vector from object center to click point
    const dx = worldX - obj.center.x;
    const dy = worldY - obj.center.z;

    // Rotate into object-local space
    const localX = dx * cosA + dy * sinA;
    const localY = -dx * sinA + dy * cosA;

    return Math.abs(localX) <= width / 2 && Math.abs(localY) <= depth / 2;
  }

  // No rotation — simple axis-aligned bounding box
  const halfWidth = width / 2;
  const halfDepth = depth / 2;

  return (
    worldX >= obj.center.x - halfWidth &&
    worldX <= obj.center.x + halfWidth &&
    worldY >= obj.center.z - halfDepth &&
    worldY <= obj.center.z + halfDepth
  );
};

/**
 * Checks if a point is within a sink's bounds
 * @param {number} worldX - World X coordinate
 * @param {number} worldY - World Y coordinate (Z in 3D)
 * @param {Object} sink - Sink object with position property
 * @returns {boolean} True if point is inside sink bounds
 */
export const isPointInSinkBounds = (worldX, worldY, sink) => {
  // Support both position and center fields
  const sinkPos = sink.position || sink.center;

  if (!sinkPos || sinkPos.x === undefined || sinkPos.z === undefined) {
    return false;
  }

  // Use circular bounds for sink (radius of ~0.35 meters / ~14 inches)
  // This provides a reasonable click target for standard sink sizes
  const sinkRadius = 0.35;

  const distance = Math.sqrt(
    Math.pow(worldX - sinkPos.x, 2) + Math.pow(worldY - sinkPos.z, 2)
  );

  return distance <= sinkRadius;
};

/**
 * The floor-plan canvas's view, in CSS pixels: what `UVFloorPlanCanvas`
 * reports through `onViewChange`, and all a layer drawn over the canvas needs
 * to land on the same pixels.
 * @typedef {Object} PlanView
 * @property {number} width - Canvas CSS width
 * @property {number} height - Canvas CSS height
 * @property {{x: number, y: number}} pan - Pan offset
 * @property {number} scale - Pixels per metre
 * @property {number} rotation - Rotation in degrees
 */

/**
 * Screen → world for a canvas view.
 * @param {number} screenX
 * @param {number} screenY
 * @param {PlanView} view
 * @returns {{x: number, y: number}} World x and z (as `y`)
 */
export const viewScreenToWorld = (screenX, screenY, view) => {
  const { width, height, pan, scale, rotation } = view;
  const centerX = width / 2 + pan.x;
  const centerY = height / 2 + pan.y;

  // Calculate position relative to center
  const translatedX = screenX - centerX;
  const translatedY = screenY - centerY;

  // Apply inverse rotation
  const rotationRadians = (rotation * Math.PI) / 180;
  const rotatedX =
    translatedX * Math.cos(-rotationRadians) -
    translatedY * Math.sin(-rotationRadians);
  const rotatedY =
    translatedX * Math.sin(-rotationRadians) +
    translatedY * Math.cos(-rotationRadians);

  // Apply inverse scale to get world coordinates
  return { x: rotatedX / scale, y: rotatedY / scale };
};

/**
 * World → screen for a canvas view.
 * @param {number} worldX
 * @param {number} worldY - World z
 * @param {PlanView} view
 * @returns {{x: number, y: number}} Screen coordinates
 */
export const worldToViewScreen = (worldX, worldY, view) => {
  const { width, height, pan, scale, rotation } = view;
  // Apply scale to get to scaled coordinates
  const scaledX = worldX * scale;
  const scaledY = worldY * scale;

  // Apply rotation
  const rotationRadians = (rotation * Math.PI) / 180;
  const rotatedX =
    scaledX * Math.cos(rotationRadians) - scaledY * Math.sin(rotationRadians);
  const rotatedY =
    scaledX * Math.sin(rotationRadians) + scaledY * Math.cos(rotationRadians);

  return {
    x: rotatedX + width / 2 + pan.x,
    y: rotatedY + height / 2 + pan.y,
  };
};

// A canvas element's CSS size (its backing store is scaled by the DPR).
const canvasView = (canvas, pan, scale, rotation) => {
  const dpr = window.devicePixelRatio || 1;
  return {
    width: canvas.width / dpr,
    height: canvas.height / dpr,
    pan,
    scale,
    rotation,
  };
};

/**
 * Converts screen coordinates to world coordinates
 * @param {number} screenX - Screen X coordinate
 * @param {number} screenY - Screen Y coordinate
 * @param {HTMLCanvasElement} canvas - Canvas element
 * @param {Object} pan - Pan offset {x, y}
 * @param {number} scale - Zoom scale
 * @param {number} rotation - Rotation in degrees
 * @returns {Object} World coordinates {x, y}
 */
export const screenToWorldCoordinates = (
  screenX,
  screenY,
  canvas,
  pan,
  scale,
  rotation
) =>
  viewScreenToWorld(screenX, screenY, canvasView(canvas, pan, scale, rotation));

/**
 * Converts world coordinates to screen coordinates
 * @param {number} worldX - World X coordinate
 * @param {number} worldY - World Y coordinate
 * @param {HTMLCanvasElement} canvas - Canvas element
 * @param {Object} pan - Current pan offset {x, y}
 * @param {number} scale - Current zoom scale
 * @param {number} rotation - Current rotation in degrees
 * @returns {Object} Screen coordinates {x, y}
 */
export const worldToScreenCoordinates = (
  worldX,
  worldY,
  canvas,
  pan,
  scale,
  rotation
) =>
  worldToViewScreen(worldX, worldY, canvasView(canvas, pan, scale, rotation));
